import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { createNotice } from '../../../models/noticesModel';
import { toast } from 'react-toastify';
import WysiwygEditor from '../../../components/common/WysiwygEditor';
import api from '../../../services/api';

function NoticeCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    notice_title: '',
    notice_text: '',
    notice_category_id: '',
    notice_estimated: '',
    notice_status: '1',
    notice_shutdown: '',
    notice_start: '',
    notice_end: ''
  });

  // Load categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/categories', {
          params: { group_id: 2 } // Group 2 is for Notices
        });
        
        if (response.data.status === 1 && response.data.data) {
          setCategories(response.data.data);
          // Set first category as default if available
          if (response.data.data.length > 0) {
            setFormData(prev => ({
              ...prev,
              notice_category_id: response.data.data[0].category_id
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error('Failed to load categories');
      }
    };
    
    fetchCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.notice_title.trim()) {
      toast.error('Notice title is required');
      return;
    }

    if (!formData.notice_text.trim()) {
      toast.error('Notice content is required');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        files: files
      };
      
      const response = await createNotice(submitData);
      
      if (response.status === 1) {
        toast.success('Notice created successfully');
        navigate('/dashboard/notices');
      } else {
        throw new Error(response.message || 'Failed to create notice');
      }
    } catch (err) {
      console.error('Error creating notice:', err);
      toast.error(err.message || 'Failed to create notice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Create Notice</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column: Category and Status */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow border space-y-4">
              {/* Category */}
              <div>
                <label htmlFor="notice_category_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  id="notice_category_id"
                  name="notice_category_id"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                  value={formData.notice_category_id}
                  onChange={handleInputChange}
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Estimated Time - shown by default, hidden when shutdown categories selected */}
              {![14, 117].includes(parseInt(formData.notice_category_id)) && (
                <div>
                  <label htmlFor="notice_estimated" className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Time
                  </label>
                  <input
                    type="text"
                    id="notice_estimated"
                    name="notice_estimated"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.notice_estimated}
                    onChange={handleInputChange}
                    placeholder="e.g., 2 hours"
                  />
                </div>
              )}

              {/* Status */}
              <div>
                <label htmlFor="notice_status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  id="notice_status"
                  name="notice_status"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                  value={formData.notice_status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="1">Active</option>
                  <option value="0">Resolved</option>
                </select>
              </div>
            </div>
          </div>

          {/* Center/Right Columns: Main Form */}
          <div className="lg:col-span-3">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notice Details</h3>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="notice_title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="notice_title"
                    name="notice_title"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.notice_title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter notice title"
                  />
                </div>

                {/* Shutdown Date/Time Fields - shown only for shutdown categories (14, 117) */}
                {[14, 117].includes(parseInt(formData.notice_category_id)) && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="notice_shutdown" className="block text-sm font-medium text-gray-700 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        id="notice_shutdown"
                        name="notice_shutdown"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                        value={formData.notice_shutdown}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label htmlFor="notice_start" className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        id="notice_start"
                        name="notice_start"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                        value={formData.notice_start}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label htmlFor="notice_end" className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        id="notice_end"
                        name="notice_end"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                        value={formData.notice_end}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                )}

                {/* Content */}
                <div>
                  <label htmlFor="notice_text" className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <div className="border border-gray-300 rounded-md" style={{ minHeight: '300px' }}>
                    <WysiwygEditor
                      value={formData.notice_text}
                      onChange={(content) => setFormData(prev => ({
                        ...prev,
                        notice_text: content
                      }))}
                    />
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-1">
                    Image <span className="text-xs text-gray-500">(Optional)</span>
                  </label>
                  <input
                    id="file"
                    name="file"
                    type="file"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                  />
                  {files.length > 0 && (
                    <p className="mt-1 text-xs text-gray-600">Selected: {files[0].name}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div>
                  <button 
                    type="submit" 
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#28a745] hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default NoticeCreate; 